import { useEffect, useRef, useState, type ReactNode } from 'react';
import { IconButton } from '@/components/ui/IconButton';
import { IconDownload, IconListBullet } from '@/components/ui/Icon';
import { useReducedMotion } from '@/shared/hooks/useReducedMotion';
import { downloadCsv } from './format';
import { chartFileName, downloadChartPng } from './exportChart';
import { toast } from '@/shared/store/toastStore';

export const PNG_FAILED = 'The chart image could not be made. Try again, or download the CSV.';

// One frame for every recharts chart on Analytics and Home (charts-05): a
// figure named by its title and a one-line summary, the same numbers as a
// table (visually hidden until "View as table"), and the series' motion
// switched off under prefers-reduced-motion (charts-11). The chart itself is
// a render prop so each chart keeps its own recharts tree.
// summary: false keeps a derived line (a trend, a running total) out of the
// figure's spoken total; it still has a column and a legend button.
// narrowHidden: the chart drops this series when narrow (under 420px); its
// legend button then shows it off and unavailable rather than pressed.
export type FrameSeries = { key: string; name: string; color: string; summary?: boolean; narrowHidden?: boolean };
export type FrameCtx = { animate: boolean; hidden: (key: string) => boolean; narrow: boolean };
type Row = Record<string, unknown>;

const isBlank = (v: unknown) => v == null || v === '';
const num = (v: unknown) => (typeof v === 'number' ? v : Number(v) || 0);

export function chartSummary(data: Row[], series: FrameSeries[], format: (n: number) => string): string {
  if (!data.length) return 'no data';
  return series.filter((s) => s.summary !== false).map((s) => `${s.name} ${format(data.reduce((t, r) => t + num(r[s.key]), 0))}`).join(', ');
}

export function ChartFrame({ title, data, series, xKey = 'month', xLabel = 'Month', format, height, tools = true, legend = true, children }: {
  title: string;
  data: Row[];
  series: FrameSeries[];
  xKey?: string;
  xLabel?: string;
  format: (n: number) => string;
  height: number | string;
  tools?: boolean;
  // false: no key under the plot, for one series its card's title already names.
  legend?: boolean;
  children: (ctx: FrameCtx) => ReactNode;
}) {
  const reduced = useReducedMotion();
  const [asTable, setAsTable] = useState(false);
  const figRef = useRef<HTMLElement>(null);
  const [hidden, setHidden] = useState<ReadonlySet<string>>(() => new Set());
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const el = figRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver((entries) => { const w = entries[0]?.contentRect.width; if (w != null) setWidth(Math.round(w)); });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const toggle = (key: string) => setHidden((prev) => { const next = new Set(prev); if (next.has(key)) next.delete(key); else next.add(key); return next; });
  const downloadCsvNow = () => downloadCsv(
    chartFileName(title, 'csv'),
    [xLabel, ...series.map((s) => s.name)],
    data.map((r) => [String(r[xKey] ?? ''), ...series.map((s) => (isBlank(r[s.key]) ? '' : num(r[s.key])))]),
  );
  const narrow = width > 0 && width < 420;
  const ctx: FrameCtx = { animate: !reduced, hidden: (key) => hidden.has(key), narrow };
  const downloadPngNow = async () => {
    if (!figRef.current) return;
    // A failure (no chart drawn yet, a browser that refuses the canvas) is
    // announced through the office toast, a polite status region.
    // The image carries the title and the key of the series on show.
    const shown = series.filter((s) => !hidden.has(s.key) && !(narrow && s.narrowHidden === true)).map((s) => ({ name: s.name, color: s.color }));
    if (!(await downloadChartPng(figRef.current, chartFileName(title, 'png'), { title, series: shown }))) toast.info(PNG_FAILED);
  };
  const table = (
    <table className={asTable ? 'w-full text-body' : 'sr-only'}>
      <caption className="sr-only">{title}</caption>
      <thead>
        <tr className="border-b border-border-light text-secondary">
          <th scope="col" className="py-1 text-left font-medium">{xLabel}</th>
          {series.map((s) => <th key={s.key} scope="col" className="py-1 text-right font-medium">{s.name}</th>)}
        </tr>
      </thead>
      <tbody>
        {data.map((r, i) => (
          <tr key={i} className="border-b border-border-light">
            <th scope="row" className="py-1 text-left font-normal text-primary-text">{String(r[xKey] ?? '')}</th>
            {series.map((s) => (
              <td key={s.key} className="py-1 text-right font-numeric tabular-nums">{isBlank(r[s.key]) ? '' : format(num(r[s.key]))}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
  return (
    <figure ref={figRef} data-chart aria-label={`${title}: ${chartSummary(data, series, format)}`} style={{ height }} className="group relative m-0 flex flex-col">
      {tools && (
        <div className="absolute right-0 top-0 z-10 flex gap-1 rounded-full bg-surface opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100 [@media(hover:none)]:opacity-100">
          <IconButton label="View as table" aria-pressed={asTable} onClick={() => setAsTable((v) => !v)}>
            <IconListBullet size={13} />
          </IconButton>
          <IconButton label="Download CSV" onClick={downloadCsvNow}><IconDownload size={13} /></IconButton>
          <IconButton label="Download PNG" onClick={() => void downloadPngNow()}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="3" y="4" width="18" height="16" rx="2" /><circle cx="9" cy="10" r="2" /><path d="m21 16-5-5-9 9" /></svg>
          </IconButton>
        </div>
      )}
      {/* pt-7 clears the tools pill (26px, top right) so the last header cell shows. */}
      {asTable ? <div className={`min-h-0 flex-1 overflow-auto${tools ? ' pt-7' : ''}`}>{table}</div> : (
        <>
          <div data-part="plot" className="min-h-0 w-full flex-1">{children(ctx)}</div>
          {table}
          {legend && <div role="group" aria-label={`${title} series`} className="flex shrink-0 flex-wrap justify-center gap-x-3 gap-y-0.5 pt-1">
            {series.map((s) => {
              const dropped = narrow && s.narrowHidden === true;
              const off = dropped || hidden.has(s.key);
              return (
                <button key={s.key} type="button" aria-pressed={!off} aria-disabled={dropped || undefined}
                  title={dropped ? 'Shown when the chart is wider' : undefined}
                  onClick={dropped ? undefined : () => toggle(s.key)}
                  className={`focus-ring inline-flex min-h-6 items-center gap-1.5 rounded px-1 text-meta ${off ? 'text-muted line-through' : 'text-secondary hover:text-primary-text'}`}>
                  <span aria-hidden="true" className="h-2.5 w-2.5 shrink-0 rounded-sm" style={{ background: s.color }} />
                  {s.name}
                </button>
              );
            })}
          </div>}
        </>
      )}
    </figure>
  );
}
